import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import RelatedCalculators from '../components/RelatedCalculators';
import { detectLocaleSync } from '../locales/config';

const COLORS = { bg: "#0a0a0f", text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0", border: "#1e1e2e" };

const faqItems = [
  {
    q: 'How does a student loan payoff calculator work?',
    a: "Enter your student loan balance, interest rate, and current monthly payment. The calculator shows exactly when you'll be debt-free and how much total interest you'll pay — plus how extra payments change both numbers.",
  },
  {
    q: 'Should I pay off student loans early or invest?',
    a: 'If your student loan interest rate is above 6–7%, extra payments generally beat investing in a standard savings account. If it\'s below that, investing in index funds may return more over time. Federal loans with rates below 5% are a common case where investing wins.',
  },
  {
    q: 'What is the fastest way to pay off student loans?',
    a: 'The Debt Avalanche method — targeting your highest-rate loan first — saves the most interest. If you have multiple loans, add all of them and let the calculator show the optimal payoff order.',
  },
  {
    q: 'Can I enter multiple student loans at once?',
    a: 'Yes — click "+ Add debt" to enter each loan separately. The calculator handles multiple loans with different rates and balances, giving you a combined payoff plan.',
  },
  {
    q: 'Does income-driven repayment show in the calculator?',
    a: 'The calculator works with any fixed monthly payment. Enter the payment amount from your current repayment plan and use the Extra Payment scenarios to see the impact of paying more.',
  },
];

export default function StudentLoanCalculator() {
  const locale = detectLocaleSync();
  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: COLORS.text }}>
      <Helmet>
        <title>Free Student Loan Payoff Calculator — ClearMyDebt</title>
        <meta name="description" content="Calculate exactly when you'll pay off your student loans. Enter your balance, interest rate, and payment — see your debt-free date and total interest saved instantly." />
        <link rel="canonical" href="https://clearmydebt.app/student-loan-payoff-calculator/" />
        <meta property="og:title" content="Free Student Loan Payoff Calculator — ClearMyDebt" />
        <meta property="og:description" content="See your student loan payoff date and how much interest extra payments save. Free, instant, no signup." />
        <meta property="og:url" content="https://clearmydebt.app/student-loan-payoff-calculator/" />
        <meta property="og:image" content="https://clearmydebt.app/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://clearmydebt.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        })}</script>
      </Helmet>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px 0' }}>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.accent, fontWeight: 700, marginBottom: '12px' }}>
          <a href="/" style={{ color: COLORS.accent, textDecoration: 'none' }}>ClearMyDebt</a>
        </div>
        <h1 style={{ fontSize: 'clamp(24px,5vw,36px)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px', background: `linear-gradient(135deg,#fff,${COLORS.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Free Student Loan Payoff Calculator
        </h1>
        <p style={{ color: COLORS.muted, fontSize: '1.05rem', marginBottom: 0, lineHeight: 1.6 }}>
          Enter your student loan balance, interest rate, and monthly payment to see exactly
          when you'll be debt-free — and how much interest you'll save by paying extra each month.
        </p>
      </div>

      <Calculator
        defaultDebtType="Student Loan"
        lockedDebtType={false}
        locale={locale}
        showHelmet={false}
        showHeader={false}
      />

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px 64px' }}>
        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          How to use this calculator
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          Enter your current loan balance, interest rate (APR or stated rate), and your monthly payment.
          If you have multiple student loans, click "+ Add debt" to add each one separately.
          Then use the "Extra Payment" scenarios to see how much faster you pay off your loans
          and how many dollars you save in interest.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          How student loan interest works
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          Most student loans accrue interest daily based on your outstanding principal balance.
          Federal loan rates in the US range from around 5% to 8.5% depending on loan type and
          graduation year. Private loans can go higher. On a $30,000 balance at 6.5%, paying only
          the minimum over 10 years costs roughly $10,700 in interest alone — extra payments directly
          cut that number.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 20px' }}>
          Frequently Asked Questions
        </h2>
        {faqItems.map(({ q, a }) => (
          <div key={q} style={{ marginBottom: 20, padding: '16px', background: '#13131a', borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <h3 style={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>{q}</h3>
            <p style={{ color: COLORS.muted, lineHeight: 1.6, margin: 0 }}>{a}</p>
          </div>
        ))}

      </div>

      <RelatedCalculators locale={locale} exclude="/student-loan-payoff-calculator/" />
    </div>
  );
}
